/**
 * 送货条码标签打印（po_cfm.vue 使用）。
 * 70mm 宽 × 50mm 高，8 点/mm；每次打印一条记录。
 * 上区：供应商、采购单号、送货单号、送货日期及二维码。
 * 下区：物料、规格、仓库/仓位、采购单位/数量、备注，末行独立显示大字物料编码。
 */
import { printCanvas, connectPrinter } from "./btPrinter";
export { isSupported, getUnsupportedReason } from "./btPrinter";

// ================================================================
//  布局常量
// ================================================================
var LABEL_WIDTH_MM = 70;
var LABEL_HEIGHT_MM = 50;
var DOTS_PER_MM = 8;
var W = LABEL_WIDTH_MM * DOTS_PER_MM;
var H = LABEL_HEIGHT_MM * DOTS_PER_MM;

var SAFE = 8;
var MARGIN = 12;
var FS_UP = 24;
var FS_DOWN = 24;
var FS_CODE = 32;
var CODE_ROW_H = 40;
var TOP_Y = 8;
var UP_ROW_H = 30;
var QR_SIZE = 128;
var BAR_W = 128;
var BAR_X = W - MARGIN - BAR_W;
var LEFT_W = BAR_X - MARGIN - 12;
var DOWN_ROW_H = 28;
var WRAP_LINE_H = 26;

// ================================================================
//  工具函数
// ================================================================

function fontOf(size, bold, weight) {
  return (bold ? (weight || 500) + " " : "") + size + 'px "Microsoft YaHei",sans-serif';
}

// 字重由字体设置控制，不额外叠加描边。
function drawLabelText(ctx, text, x, y) {
  ctx.fillText(text, x, y);
}

/**
 * 单行文本自适应：超宽时缩小字号（下限16），仍超宽则截断加省略号
 * 注意：返回时 ctx.font 已是最终字体
 * @returns {string} 处理后的文本
 */
function fitText(ctx, text, maxW, fs, bold, weight) {
  var s = fs;
  ctx.font = fontOf(s, bold, weight);
  var t = String(text === null || text === undefined || text === "" ? "-" : text);
  while (s > 16 && ctx.measureText(t).width > maxW) {
    s -= 1;
    ctx.font = fontOf(s, bold, weight);
  }
  if (ctx.measureText(t).width > maxW) {
    while (t.length && ctx.measureText(t + "…").width > maxW) {
      t = t.slice(0, -1);
    }
    t += "…";
  }
  return t;
}

/** 单行文本绘制（自适应字号/省略号） */
function drawFitText(ctx, text, x, y, maxW, fs, bold) {
  drawLabelText(ctx, fitText(ctx, text, maxW, fs, bold), x, y);
}

/**
 * 按最大宽度换行，最多 maxLines 行，仍超出则末行末尾加省略号
 * 注意：返回时 ctx.font 为传入字体
 * @returns {string[]} 文本行数组
 */
function wrapText(ctx, text, maxW, font, maxLines) {
  ctx.font = font;
  var s = String(text === null || text === undefined || text === "" ? "-" : text);
  if (ctx.measureText(s).width <= maxW) return [s];

  var lines = [];
  var cur = "";
  var overflow = false;
  for (var i = 0; i < s.length; i++) {
    var ch = s[i];
    if (cur && ctx.measureText(cur + ch).width > maxW) {
      if (lines.length >= maxLines - 1) {
        lines.push(cur);
        overflow = true;
        break;
      }
      lines.push(cur);
      cur = ch;
    } else {
      cur += ch;
    }
  }
  if (cur && !overflow) lines.push(cur);

  if (overflow) {
    var last = lines.length ? lines[lines.length - 1] : "";
    while (last.length > 1 && ctx.measureText(last + "…").width > maxW) {
      last = last.slice(0, -1);
    }
    if (lines.length) lines[lines.length - 1] = last + "…";
    else lines = [last + "…"];
  }
  return lines.length ? lines : ["-"];
}

// ================================================================
//  Canvas 渲染
// ================================================================

/**
 * 渲染标签 Canvas（二维码异步绘制，故返回 Promise）
 * @param {Object} d - 标签数据
 * @returns {Promise<HTMLCanvasElement>}
 */
function renderLabelCanvas(d) {
  var canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  var ctx = canvas.getContext("2d");

  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, W, H);
  // 裁剪安全区，避免打印机边缘裁切
  ctx.beginPath();
  ctx.rect(SAFE, 0, W - SAFE * 2, H);
  ctx.clip();

  ctx.textBaseline = "top";
  ctx.fillStyle = "#000";

  var warehouse = d.stknm === null || d.stknm === undefined || d.stknm === "" ? "-" : String(d.stknm);
  var location = d.plcnm === null || d.plcnm === undefined ? "" : String(d.plcnm).trim();
  var warehouseLocation = warehouse + (location && location !== "-" ? "(" + location + ")" : "");

  var downRows = [
    // [{ label: "供应商：", value: d.supnm, span: 2, wrap: true }],
    [{ label: "物料名称：", value: d.matnm, span: 2, wrap: true }],
    [{ label: "规格描述：", value: d.specnm, span: 2, wrap: true, maxLines: 2 }],
    [
      { label: "仓库(仓位)：", value: warehouseLocation, span: 2 },
    ],
    [
      { label: "采购单位：", value: d.punit },
      { label: "采购数量：", value: d.poqty },
    ],
    [
      { label: "送货备注：", value: d.rmk, span: 2, wrap: true },
    ],
    [
      { label: "物料编码：", value: d.code, span: 2, fs: FS_CODE, weight: 700, height: CODE_ROW_H },
    ],

  ];
  function baseRowHeight(row) {
    return row.reduce(function(height, cell) {
      return Math.max(height, cell.height || DOWN_ROW_H);
    }, DOWN_ROW_H);
  }
  var downHeight = downRows.reduce(function(total, row) { return total + baseRowHeight(row); }, 0);

  // ---------- 上区：左侧 4 行信息 ----------
  var upRows = [
    ["供应商：", d.supnm],
    ["采购单号：", d.refpono],
    ["送货单号：", d.idno],
    ["送货日期：", d.adddttm],
 
  ];

  // 标签列宽取最长标签文字
  var labelW = 0;
  ctx.font = FS_UP + 'px "Microsoft YaHei",sans-serif';
  upRows.forEach(function(r) {
    labelW = Math.max(labelW, ctx.measureText(r[0]).width);
  });
  labelW += 4;
  var valW = LEFT_W - labelW;

  // 上区换行行高（第2行起），避免与下一行重叠
  var UP_WRAP_H = 26;
  var uy = TOP_Y;
  // 先为下区全部基础行和上下分隔间距留位，避免大字换行挤出纸面。
  var upperExtraLines = Math.max(0, Math.floor(
    (H - 4 - TOP_Y - upRows.length * UP_ROW_H - 8 -
      downHeight - 4 - WRAP_LINE_H) / UP_WRAP_H
  ));
  upRows.forEach(function(r) {
    var y = uy + (UP_ROW_H - FS_UP) / 2;
    var font = "500 " + FS_UP + 'px "Microsoft YaHei",sans-serif';
    ctx.font = font;
    drawLabelText(ctx, r[0], MARGIN, y);
    // 值超宽时换行（最多2行，仍超出截断加省略号）
    var lines = wrapText(ctx, r[1] || "-", valW, font, upperExtraLines > 0 ? 2 : 1);
    ctx.font = font;
    lines.forEach(function(ln, li) {
      drawLabelText(ctx, ln, MARGIN + labelW, y + li * UP_WRAP_H);
    });
    upperExtraLines -= lines.length - 1;
    uy += UP_ROW_H + (lines.length - 1) * UP_WRAP_H;
  });

  // ---------- 右侧二维码 ----------
  var qrY = TOP_Y;
  var qrX = BAR_X + (BAR_W - QR_SIZE) / 2; // 在右侧区域内居中
  ctx.lineWidth = 2;
  ctx.strokeStyle = "#000";
  ctx.strokeRect(qrX, qrY, QR_SIZE, QR_SIZE);
  // ---------- 上下区分隔横线 ----------
  var sepY = Math.max(uy, qrY + QR_SIZE) + 4;
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.moveTo(MARGIN, sepY);
  ctx.lineTo(W - MARGIN, sepY);
  ctx.stroke();

  // ---------- 下区：整行(可换行) + 2 列 ----------
  // cell: { label, value, span:1|2(占列数), wrap:true 允许换行(最多2行) }
  var colW = (W - MARGIN * 2) / 2;
  var col2X = MARGIN + colW;

  var y = sepY + 4;
  // 保留全部基础行和底边距；额外换行只使用纸张剩余高度。
  var extraLines = Math.max(0, Math.floor(
    (H - 4 - (y + downHeight + 4)) / WRAP_LINE_H
  ));
  downRows.forEach(function(row) {
    // 1) 先计算每个单元格的文本行，用于确定行高
    var infos = row.map(function(cell, ci) {
      var w = cell.span === 2 ? colW * 2 : colW;
      var x = ci === 0 ? MARGIN : col2X;
      var fs = cell.fs || FS_DOWN;
      var labelFont = fontOf(fs, true, cell.weight);
      ctx.font = labelFont;
      var lw = ctx.measureText(cell.label).width + 6;
      var valW = w - lw - 10;
      var lines = cell.wrap
        ? wrapText(ctx, cell.value, valW, labelFont, Math.min(cell.maxLines || 2, extraLines > 0 ? 2 : 1))
        : [fitText(ctx, cell.value, valW, fs, true, cell.weight)];
      return { cell: cell, x: x, lw: lw, lines: lines, font: ctx.font, labelFont: labelFont, fs: fs };
    });

    // 2) 行高按最大行数累加
    var maxLines = 1;
    infos.forEach(function(info) {
      if (info.lines.length > maxLines) maxLines = info.lines.length;
    });
    extraLines -= maxLines - 1;
    var lineH = infos.reduce(function(height, info) { return Math.max(height, info.fs + 2); }, WRAP_LINE_H);
    var rowH = baseRowHeight(row) + (maxLines - 1) * WRAP_LINE_H;
    var yText = y + (rowH - maxLines * lineH) / 2;

    // 3) 绘制：标签常规字重，值加粗（多行时逐行输出）
    infos.forEach(function(info) {
      ctx.font = info.labelFont;
      drawLabelText(ctx, info.cell.label, info.x, yText);
      ctx.font = info.font;
      for (var li = 0; li < info.lines.length; li++) {
        drawLabelText(ctx, info.lines[li], info.x + info.lw, yText + li * lineH);
      }
    });
    y += rowH;
  });

  // ---------- 底部分隔线（不打印时间） ----------
  var footLineY = y + 4;
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(MARGIN, footLineY);
  ctx.lineTo(W - MARGIN, footLineY);
  ctx.stroke();

  // ---------- 绘制二维码图片 ----------
  return new Promise(function(resolve) {
    if (!d.qrCode) return resolve(canvas);
    var img = new Image();
    img.onload = function() {
      ctx.drawImage(img, qrX, qrY, QR_SIZE, QR_SIZE);
      resolve(canvas);
    };
    img.onerror = function() {
      // 二维码加载失败不影响标签其余内容
      resolve(canvas);
    };
    img.src = d.qrCode;
  });
}

// ================================================================
//  预览 / 打印
// ================================================================

/**
 * 生成打印预览图
 * @param {Object} d - 标签数据（含 qrCode DataURL）
 * @returns {Promise<string>} 预览图 dataURL
 */
export function renderPreview(d) {
  return renderLabelCanvas(d).then(function(canvas) {
    return canvas.toDataURL("image/png");
  });
}

/**
 * 蓝牙打印发料条码标签
 * @param {Object} d - 标签数据（含 qrCode DataURL）
 * @returns {Promise}
 */
export function printFabBarcode(d) {
  return connectPrinter().then(function() {
    return renderLabelCanvas(d);
  }).then(function(canvas) {
    return printCanvas(canvas, { protocol: "cpcl", media: "gap" });
  });
}
